import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  usePhaseAction as useCorePhaseAction,
  useUpdateActionStatus,
  shouldUpdateStatus,
  ACTION_STATUS,
  type Action,
  type ActionHelpers,
  type ActionStatusType,
} from "@core/features/actions";
import { useDialog, useSupportSheet } from "@platform/store";
import { actionRegistry } from "../actionRegistry";

/**
 * Get the onClick handler and display properties for a phase action.
 * Creates the helpers with platform-specific implementations.
 *
 * @param action - The action to get handler for
 * @param timezone - User's timezone (IANA format, e.g., "America/New_York")
 */
export function usePhaseAction(action: Action, timezone: string = "UTC") {
  const navigate = useNavigate();
  const { openDialog } = useDialog();
  const { open: openSheet } = useSupportSheet();

  // Create mutation for updating status
  const { mutate: updateStatusMutation } = useUpdateActionStatus({
    actionId: action.id,
    status: ACTION_STATUS.IN_PROGRESS, // Default, will be overridden
  });

  // Create stable helpers object
  const helpers: ActionHelpers = useMemo(
    () => ({
      navigate,
      openDialog: (name: string, props?: Record<string, unknown>) => {
        openDialog(name, props);
      },
      openSheet,
      updateStatus: (status: ActionStatusType) => {
        // Guard against invalid status transitions:
        // - Don't re-set "In Progress" if already "In Progress"
        // - Don't downgrade from "Completed" to "In Progress"
        if (shouldUpdateStatus(action, status)) {
          updateStatusMutation({ status });
        }
      },
    }),
    [navigate, openDialog, openSheet, updateStatusMutation, action]
  );

  return useCorePhaseAction(actionRegistry, action, helpers, timezone);
}
